import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, DatePicker, Input, InputNumber, Select, message } from 'antd';
import dayjs from 'dayjs';
import { ArrowLeft, Plus, Search, Trash2 } from 'lucide-react';
import Layout from '../../Layout';
import { khoApi, newLineId, useKho } from './khoStore';
import { NO_LOT, countSheet, docById, fmtMoney, fmtNum, itemById, validateDoc, type DocLine, type StockDoc } from './khoLogic';
import { ExpiryText, PageHeader, Panel, docUrl, useKhoPerms } from './khoUi';

type Row = DocLine & { extra?: boolean };

/** Phiếu kiểm kho: đếm thực tế từng lô, chênh lệch được điều chỉnh khi phiếu được duyệt */
export default function CountFormPage() {
  const { search } = useLocation();
  return <CountForm key={search} />;
}

function CountForm() {
  const s = useKho();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { canCreate, me } = useKhoPerms();
  const editId = params.get('id') || undefined;
  const editing = editId ? docById(s, editId) : null;
  const now = Date.now();

  const [wh, setWh] = useState<string | undefined>(editing?.warehouseId || params.get('wh') || undefined);
  const [rows, setRows] = useState<Row[]>(() => (editing ? editing.lines : wh ? sheet(wh) : []));
  const [note, setNote] = useState(editing?.note || '');
  const [date, setDate] = useState(dayjs(editing?.date ?? now));
  const [q, setQ] = useState('');
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  function sheet(id: string): Row[] {
    return countSheet(s, id).map(l => ({ ...l, id: newLineId(), counted: undefined }));
  }
  const setRow = (id: string, p: Partial<Row>) => setRows(rs => rs.map(r => (r.id === id ? { ...r, ...p } : r)));

  const stats = useMemo(() => {
    const counted = rows.filter(r => r.counted !== undefined && r.counted !== null);
    const diffs = counted.filter(r => (r.counted ?? 0) !== (r.systemQty ?? 0));
    const plus = diffs.reduce((t, r) => t + Math.max(0, (r.counted! - (r.systemQty ?? 0)) * r.unitCost), 0);
    const minus = diffs.reduce((t, r) => t + Math.max(0, ((r.systemQty ?? 0) - r.counted!) * r.unitCost), 0);
    return { total: rows.length, counted: counted.length, diffs: diffs.length, plus, minus };
  }, [rows]);

  if (editId && (!editing || editing.type !== 'count' || (editing.status !== 'draft' && editing.status !== 'rejected'))) {
    return <Layout><Alert type="warning" showIcon message="Phiếu kiểm không sửa được nữa (đã gửi duyệt hoặc không tồn tại)." action={editing ? <Link to={docUrl(editing.id)}>Xem phiếu</Link> : undefined} /></Layout>;
  }
  if (!canCreate) return <Layout><Alert type="warning" showIcon message="Bạn không có quyền lập phiếu kiểm kho." /></Layout>;

  const visible = rows.filter(r => {
    const item = itemById(s, r.itemId);
    const k = q.trim().toLowerCase();
    if (k && !(item?.name.toLowerCase().includes(k) || item?.code.toLowerCase().includes(k) || r.lotNo.toLowerCase().includes(k))) return false;
    if (onlyDiff && (r.counted === undefined || r.counted === null || r.counted === r.systemQty)) return false;
    return true;
  });

  const save = (submit: boolean) => {
    if (!wh) { setErrors(['Chọn kho cần kiểm']); return; }
    const lines: DocLine[] = rows.filter(r => r.itemId).map(({ extra, ...r }) => ({ ...r, lotNo: r.lotNo.trim() || NO_LOT }));
    const at = date.isSame(now, 'day') ? now : date.hour(12).valueOf();
    const preview: StockDoc = { id: editId || '__new', code: '', type: 'count', status: 'draft', date: at, warehouseId: wh, lines, createdBy: me, createdAt: now, log: [] };
    const errs = submit ? validateDoc(s, preview, now) : lines.length ? [] : ['Kho chưa có hàng để kiểm'];
    if (errs.length) { setErrors(errs.slice(0, 8).concat(errs.length > 8 ? [`… và ${errs.length - 8} dòng khác chưa nhập số đếm`] : [])); return; }
    const r = khoApi.saveDoc({ id: editId, type: 'count', date: at, warehouseId: wh, lines, note: note.trim() || undefined }, me);
    if (!r.ok || !r.id) { setErrors(r.errors || ['Không lưu được']); return; }
    if (submit) {
      const sub = khoApi.submitDoc(r.id, me);
      if (!sub.ok) { setErrors(sub.errors || []); return; }
      message.success('Đã gửi duyệt phiếu kiểm kho');
    } else message.success('Đã lưu nháp phiếu kiểm');
    navigate(docUrl(r.id));
  };

  const itemOptions = s.items.filter(i => i.active).map(i => ({ value: i.id, label: `${i.code} · ${i.name}` }));

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title={editing ? `Sửa phiếu kiểm ${editing.code}` : 'Kiểm kho'}
          desc="Đếm thực tế từng lô. Tồn sổ sách chỉ được điều chỉnh theo chênh lệch khi phiếu kiểm được duyệt."
          extra={<Button icon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>Quay lại</Button>} />

        {editing?.status === 'rejected' && <Alert type="error" showIcon message={`Phiếu bị từ chối: ${editing.rejectReason || ''}`} />}
        {errors.length > 0 && <Alert type="error" showIcon closable onClose={() => setErrors([])} message="Chưa lưu được" description={<ul className="list-disc pl-4 m-0">{errors.map(e => <li key={e}>{e}</li>)}</ul>} />}

        <Panel title="Thông tin kiểm kho">
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Kho kiểm <span className="text-rose-500">*</span></label>
              <Select className="w-full" placeholder="Chọn kho" value={wh} disabled={!!editing}
                onChange={v => { setWh(v); setRows(sheet(v)); }} options={s.warehouses.map(w => ({ value: w.id, label: w.name }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Ngày kiểm <span className="text-rose-500">*</span></label>
              <DatePicker className="w-full" format="DD/MM/YYYY" allowClear={false} value={date} onChange={d => d && setDate(d)} disabledDate={d => d.isAfter(dayjs(), 'day')} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Ghi chú</label>
              <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Người kiểm, hội đồng kiểm kê…" />
            </div>
          </div>
        </Panel>

        {wh && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Đã đếm" value={`${stats.counted}/${stats.total} lô`} />
            <Stat label="Lô chênh lệch" value={`${stats.diffs}`} tone={stats.diffs ? 'text-amber-600' : ''} />
            <Stat label="Thừa (giá trị)" value={fmtMoney(stats.plus)} tone={stats.plus ? 'text-emerald-600' : ''} />
            <Stat label="Thiếu (giá trị)" value={fmtMoney(stats.minus)} tone={stats.minus ? 'text-rose-600' : ''} />
          </div>
        )}

        {wh && (
          <Panel title="Bảng kiểm"
            extra={<div className="flex flex-wrap gap-2">
              <Input allowClear size="small" className="!w-52" prefix={<Search size={13} className="text-gray-400" />} placeholder="Tìm hàng, số lô" value={q} onChange={e => setQ(e.target.value)} />
              <Button size="small" type={onlyDiff ? 'primary' : 'default'} onClick={() => setOnlyDiff(v => !v)}>Chỉ dòng lệch</Button>
              <Button size="small" onClick={() => setRows(rs => rs.map(r => (r.counted === undefined || r.counted === null ? { ...r, counted: r.systemQty } : r)))}>Điền số sổ sách cho dòng chưa đếm</Button>
            </div>}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Mặt hàng</th>
                    <th className="text-left font-medium px-3 py-2 w-[140px]">Số lô</th>
                    <th className="text-left font-medium px-3 py-2 w-[200px]">Hạn dùng</th>
                    <th className="text-right font-medium px-3 py-2 w-[110px]">Sổ sách</th>
                    <th className="text-right font-medium px-3 py-2 w-[130px]">Thực tế</th>
                    <th className="text-right font-medium px-3 py-2 w-[100px]">Chênh lệch</th>
                    <th className="text-right font-medium px-3 py-2 w-[130px]">Giá trị lệch</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map(r => {
                    const item = itemById(s, r.itemId);
                    const has = r.counted !== undefined && r.counted !== null;
                    const diff = has ? r.counted! - (r.systemQty ?? 0) : 0;
                    return (
                      <tr key={r.id} className={`border-t border-gray-100 dark:border-gray-800 ${has && diff ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                        <td className="px-3 py-2">
                          {r.extra ? (
                            <Select className="w-full" showSearch optionFilterProp="label" placeholder="Hàng tìm thấy ngoài sổ" value={r.itemId || undefined}
                              onChange={v => setRow(r.id, { itemId: v, unitCost: itemById(s, v)?.cost ?? 0 })} options={itemOptions} />
                          ) : (
                            <><div className="font-medium">{item?.name}</div><div className="text-[11px] text-gray-400">{item?.code}</div></>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {r.extra ? <Input size="small" value={r.lotNo} placeholder="Số lô" onChange={e => setRow(r.id, { lotNo: e.target.value })} /> : <span className="font-mono text-xs">{r.lotNo}</span>}
                        </td>
                        <td className="px-3 py-2">
                          {r.extra ? <DatePicker size="small" format="DD/MM/YYYY" value={r.expiry ? dayjs(r.expiry) : null} onChange={d => setRow(r.id, { expiry: d?.format('YYYY-MM-DD') })} />
                            : <ExpiryText expiry={r.expiry} now={now} />}
                        </td>
                        <td className="px-3 py-2 text-right">{fmtNum(r.systemQty ?? 0)} <span className="text-[11px] text-gray-400">{item?.unit}</span></td>
                        <td className="px-3 py-2 text-right">
                          <InputNumber size="small" min={0} className="!w-[110px]" value={r.counted ?? null} placeholder="Đếm" onChange={v => setRow(r.id, { counted: v ?? undefined })} />
                        </td>
                        <td className={`px-3 py-2 text-right font-semibold ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-rose-600' : 'text-gray-300'}`}>
                          {has ? (diff > 0 ? `+${fmtNum(diff)}` : fmtNum(diff)) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{has && diff ? fmtMoney(diff * r.unitCost) : <span className="text-gray-300">—</span>}</td>
                        <td className="px-2 text-center">
                          {r.extra && <button type="button" aria-label="Xóa dòng" className="text-gray-400 hover:text-rose-600 p-1" onClick={() => setRows(rs => rs.filter(x => x.id !== r.id))}><Trash2 size={15} /></button>}
                        </td>
                      </tr>
                    );
                  })}
                  {!visible.length && <tr><td colSpan={8} className="text-center text-gray-400 py-8">{rows.length ? 'Không có dòng phù hợp' : 'Kho này chưa có hàng tồn'}</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-3 border-t border-gray-100 dark:border-gray-800">
              <Button size="small" icon={<Plus size={14} />} onClick={() => setRows(rs => [...rs, { id: newLineId(), itemId: '', lotNo: '', qty: 0, unitCost: 0, systemQty: 0, counted: undefined, extra: true }])}>
                Thêm hàng tìm thấy ngoài sổ sách
              </Button>
            </div>
          </Panel>
        )}

        <div className="sticky bottom-0 z-20 bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm px-4 py-3 flex justify-end gap-2">
          <Button onClick={() => navigate(-1)}>Hủy bỏ</Button>
          <Button onClick={() => save(false)} disabled={!wh}>Lưu nháp</Button>
          <Button type="primary" onClick={() => save(true)} disabled={!wh}>Hoàn tất & gửi duyệt</Button>
        </div>
      </div>
    </Layout>
  );
}

const Stat = ({ label, value, tone = '' }: { label: string; value: string; tone?: string }) => (
  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl px-4 py-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-lg font-bold ${tone || 'text-gray-900 dark:text-gray-100'}`}>{value}</div>
  </div>
);
